import React, { useState } from 'react';
import { Box, Tab, TableBody } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import moment from 'moment';

import CommonLoading from '../../../form-components/CommonLoading';
import useTransferableProducts from './hooks/useTransferableProducts';
import {
  Card,
  StyledTable,
  StyledTableContainer,
  StyledTableHead,
  HeaderCell,
  BodyRow,
  BodyCell,
  TransferRowButton,
  EmptyRowText,
  StyledTabs,
  TabsWrapper,
} from './UserTransferStyled';

// 양도 불가능한(이미 만료/소진) 상태는 버튼을 비활성화한다.
const TRANSFERABLE_STATUS = 'ACTIVE';

// expireDate(YYYY-MM-DD) 기준으로 오늘부터 남은 일수를 계산한다. 만료일 당일 포함.
const remainingDays = expireDate => {
  if (!expireDate) return '-';
  const diff = moment(expireDate).startOf('day').diff(moment().startOf('day'), 'days');
  return diff < 0 ? 0 : diff;
};

const PRODUCT_TYPE_LABEL = { TICKET: '수강권', PASS: '이용권', PRODUCT: '기간 상품', LOCKER: '라커' };
const productTypeLabel = (type, t) => t(PRODUCT_TYPE_LABEL[type] ?? '이용권');

// 양도탭 분류 필터. row.category = 수강권 CourseClassType / PASS / PRODUCT / LOCKER(#166).
// 필터 값 → 포함할 category 목록. 듀엣레슨은 개인레슨에 포함한다.
const CATEGORY_ALL = 'ALL';
const CATEGORY_GROUPS = { PRIVATE_LESSON: ['PRIVATE_LESSON', 'DUET_LESSON'] };
const matchesCategory = (filter, rowCategory) =>
  filter === CATEGORY_ALL || (CATEGORY_GROUPS[filter] ?? [filter]).includes(rowCategory);
const CATEGORY_FILTERS = [
  { value: CATEGORY_ALL, label: '전체' },
  { value: 'PRIVATE_LESSON', label: '개인레슨' },
  { value: 'GROUP_LESSON', label: '그룹' },
  { value: 'PASS', label: '이용권' },
  { value: 'OT', label: 'OT' },
  { value: 'LOCKER', label: '라커' },
  { value: 'PRODUCT', label: '운동복' },
];
const CATEGORY_LABEL = {
  PRIVATE_LESSON: '개인레슨',
  GROUP_LESSON: '그룹레슨',
  DUET_LESSON: '듀엣레슨',
  OT: 'OT',
  PASS: '이용권',
  LOCKER: '라커',
  PRODUCT: '기간 상품',
};
const categoryLabel = (row, t) => t(CATEGORY_LABEL[row.category] ?? PRODUCT_TYPE_LABEL[row.productType] ?? '-');

const TransferProductTable = ({ user, bigUser, onTransferClick, disableTransfer }) => {
  const { t } = useTranslation();
  const toast = useToast();

  // 예약(RESERVED/WAITING)이 걸린 수강권은 양도 폼을 열기 전에 버튼 단계에서 막는다.
  // 예약은 양수자에게 승계되지 않아, 다 입력한 뒤 제출 시점에 막히면 불편하기 때문.
  const handleTransferClick = row => {
    if (row.reservationCount > 0) {
      toast.error(t('예약된 수업 {{count}}건이 있습니다. 예약을 취소한 후 양도해주세요.', { count: row.reservationCount }));
      return;
    }
    onTransferClick(row);
  };

  const token = bigUser?.token;
  const memberId = user?.memberId;
  const branchId = bigUser?.branch?.branchId;

  const [category, setCategory] = useState(CATEGORY_ALL);

  const { data, isLoading } = useTransferableProducts({ memberId, branchId, token });
  const rows = (data ?? []).filter(row => matchesCategory(category, row.category));

  if (isLoading) {
    return (
      <Card elevation={2}>
        <CommonLoading minHeight={240} />
      </Card>
    );
  }

  return (
    <Card elevation={2}>
      {/* TabsWrapper 기본 높이(40px 고정)는 탭 버튼보다 작아 아래 표와 겹친다 — 높이는 내용에 맞추고 표와 간격을 둔다. */}
      <TabsWrapper sx={{ height: 'auto', marginTop: 0, marginBottom: '20px' }}>
        <StyledTabs
          value={category}
          onChange={(_, value) => setCategory(value)}
          textColor="secondary"
          indicatorColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 'auto',
            '& .MuiTab-root.MuiTab-textColorSecondary': { padding: '8px 20px', fontSize: '15px', minHeight: '36px !important' },
          }}
          aria-label="transferable product category filter">
          {CATEGORY_FILTERS.map(filter => (
            <Tab key={filter.value} value={filter.value} label={t(filter.label)} />
          ))}
        </StyledTabs>
      </TabsWrapper>
      <StyledTableContainer>
        <StyledTable size="small" aria-label="transferable product table">
          <StyledTableHead>
            <BodyRow>
              <HeaderCell align="center">{t('결제일')}</HeaderCell>
              <HeaderCell align="center">{t('양도할 회원권')}</HeaderCell>
              <HeaderCell align="center">{t('카테고리')}</HeaderCell>
              <HeaderCell align="center">{t('상품유형')}</HeaderCell>
              <HeaderCell align="center">{t('잔여일수')}</HeaderCell>
              <HeaderCell align="center">{t('잔여횟수')}</HeaderCell>
              <HeaderCell align="center">{t('판매 담당자')}</HeaderCell>
              <HeaderCell align="center">{t('양도')}</HeaderCell>
            </BodyRow>
          </StyledTableHead>

          <TableBody>
            {rows.length === 0 && (
              <BodyRow>
                <BodyCell align="center" colSpan={8} sx={{ borderRight: 'none' }}>
                  <EmptyRowText>{t('양도 가능한 회원권이 없습니다.')}</EmptyRowText>
                </BodyCell>
              </BodyRow>
            )}

            {rows.map(row => {
              const transferable = row.status === TRANSFERABLE_STATUS;
              return (
                <BodyRow key={`${row.productType}-${row.productId}`} sx={{ cursor: 'default' }}>
                  <BodyCell align="center">
                    {row.paymentDate ? moment(row.paymentDate).format('YYYY-MM-DD') : '-'}
                  </BodyCell>
                  <BodyCell align="center">
                    {row.productName}
                    {/* 락커는 쓰는 자리와 이 패스의 기간을 함께 보인다(#166) */}
                    {row.productType === 'LOCKER' && (
                      <Box sx={{ mt: '4px', fontSize: 12, color: '#1F3A5F', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {row.lockerSpot ?? t('자리 미배정')} · {row.startDate} ~ {row.expireDate}
                      </Box>
                    )}
                  </BodyCell>
                  {/* 카테고리=세부 분류(개인레슨/그룹레슨/OT/이용권/기간 상품), 상품유형=수강권/이용권/기간 상품 */}
                  <BodyCell align="center">{categoryLabel(row, t)}</BodyCell>
                  <BodyCell align="center">{productTypeLabel(row.productType, t)}</BodyCell>
                  <BodyCell align="center">{remainingDays(row.expireDate)}</BodyCell>
                  {/* PASS(이용권)는 잔여횟수 개념이 없어 remainingCount=null → '-' */}
                  <BodyCell align="center">{row.remainingCount ?? '-'}</BodyCell>
                  <BodyCell align="center">{row.trainerName || '-'}</BodyCell>
                  <BodyCell align="center">
                    <TransferRowButton
                      variant="contained"
                      disabled={!transferable || disableTransfer}
                      onClick={() => handleTransferClick(row)}>
                      {t('양도')}
                    </TransferRowButton>
                  </BodyCell>
                </BodyRow>
              );
            })}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </Card>
  );
};

export default TransferProductTable;
